'use client';

import React, { type ReactNode } from 'react';
import { cx } from '@milanote/roma/utils/cx';
import type { ButtonTransitionPhase } from './use-button-transition';

export type ButtonTransition = 'slide' | 'fade';

export type TransitionButtonContentProps = {
    phase: ButtonTransitionPhase;
    transition: ButtonTransition;
    idleContent: ReactNode;
    transitionContent: ReactNode;
    className?: string;
};

const contentClasses = 'col-start-1 row-start-1 flex items-center justify-center gap-[inherit]';
const transitionClasses = cx(
    'motion-safe:transition-all motion-safe:duration-[var(--button-transition-duration)] motion-safe:ease-linear motion-reduce:transition-none',
    // Tailwind v4 translate utilities animate the individual `translate` property, not only `transform`.
    'motion-safe:[transition-property:opacity,transform,translate]',
);

const idlePhaseClasses: Record<ButtonTransitionPhase, string> = {
    idle: 'translate-x-0 opacity-100',
    exiting: 'opacity-0',
    success: 'opacity-0 motion-safe:duration-0',
    entering: 'translate-x-0 opacity-100',
};

const transitionPhaseClasses: Record<ButtonTransitionPhase, string> = {
    idle: 'opacity-0',
    exiting: 'translate-x-0 opacity-100',
    success: 'translate-x-0 opacity-100',
    entering: 'opacity-0',
};

const slideIdleClasses: Partial<Record<ButtonTransitionPhase, string>> = {
    exiting: 'translate-x-8',
    success: '-translate-x-8',
};

const slideTransitionClasses: Partial<Record<ButtonTransitionPhase, string>> = {
    idle: '-translate-x-8',
    entering: 'translate-x-8',
};

/** Returns phase classes, adding horizontal movement only for slide transitions. */
const getPhaseClasses = (
    phase: ButtonTransitionPhase,
    transition: ButtonTransition,
    classesByPhase: Record<ButtonTransitionPhase, string>,
    slideClassesByPhase: Partial<Record<ButtonTransitionPhase, string>>,
) => cx(contentClasses, transitionClasses, classesByPhase[phase], transition === 'slide' && slideClassesByPhase[phase]);

/** Renders both states in the same grid cell so the button sizes to the widest localised state. */
export const TransitionButtonContent: React.FC<TransitionButtonContentProps> = ({
    phase,
    transition,
    idleContent,
    transitionContent,
    className,
}) => (
    <span className={cx('grid overflow-hidden', className)}>
        <span
            data-button-idle-content
            className={getPhaseClasses(phase, transition, idlePhaseClasses, slideIdleClasses)}
            aria-hidden={phase !== 'idle' ? true : undefined}
        >
            {idleContent}
        </span>
        <span
            data-button-transition-content
            className={getPhaseClasses(phase, transition, transitionPhaseClasses, slideTransitionClasses)}
            aria-live="polite"
            aria-hidden={phase === 'idle' ? true : undefined}
        >
            {transitionContent}
        </span>
    </span>
);

TransitionButtonContent.displayName = 'TransitionButtonContent';
